/**
 * Game options module.
 *
 * Manages saved game options per game type:
 * - Get saved options
 * - Set/update saved options (with validation)
 * - Compute effective options for a given player count
 */

import type { RoomStorage, BroadcastChannel } from '../../types/infrastructure.types'
import type { GameCatalogClient } from '../../types/room.types'
import type { GameSavedOptions } from '@merels/game-logic/types/options'
import { validateOptions, effectiveOptionsForCount } from '@merels/game-logic/options'
import type { GetSavedGameOptionsArgs, SetSavedGameOptionsArgs } from '../../schemas/action.schemas'
import { HOST_PLAYS_OPTION_ID, PLATFORM_GAME_OPTIONS, normalizeHostPlays, type HostPlays } from '../hosting/hosting'

export interface GameOptionsContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
  gameCatalog: GameCatalogClient
}

/**
 * Returns an empty saved options structure.
 */
function emptySavedOptions(): GameSavedOptions {
  return { global: {}, perPlayerCount: {} }
}

/**
 * Creates game options management functions for a room.
 */
export function makeGameOptions(container: GameOptionsContainer) {
  const { storage, broadcastChannel, gameCatalog } = container

  const resolveMetadata = async (gameId: string) => {
    const state = storage.getState()
    const channel = state.gameChannelPreferenceById?.[gameId] ?? 'stable'
    return (await gameCatalog.resolveVersion(gameId, channel))?.metadata
  }

  /**
   * Get saved options for a game.
   */
  const getSavedGameOptions = async (args: GetSavedGameOptionsArgs): Promise<GameSavedOptions> => {
    const { gameId } = args
    const state = storage.getState()
    const saved = state.savedGameOptionsById?.[gameId]
    return saved ?? emptySavedOptions()
  }

  /**
   * Save options for a game.
   *
   * Merges with existing options for the specified scope.
   * Validates against game definitions.
   * Broadcasts 'optionsChanged'.
   */
  const setSavedGameOptions = async (
    args: SetSavedGameOptionsArgs
  ): Promise<{
    success: boolean
    error?: string
    savedOptions?: GameSavedOptions
  }> => {
    const { gameId, scope, values, playerCount } = args

    // Validate playerCount is provided for perPlayerCount scope
    if (scope === 'perPlayerCount' && (typeof playerCount !== 'number' || !Number.isFinite(playerCount))) {
      return { success: false, error: 'playerCount required for perPlayerCount options' }
    }

    // Validate against the game's definitions plus platform-provided ones (Host plays)
    const defs = [...((await resolveMetadata(gameId))?.options ?? []), ...PLATFORM_GAME_OPTIONS]

    // Filter definitions to only those matching this scope
    const scopedDefs = defs.filter((d) => d.scope === scope)

    // Validate the values (if there are definitions)
    const ctx = { playerCount: scope === 'perPlayerCount' ? playerCount : undefined }
    const validated = scopedDefs.length > 0 ? validateOptions(scopedDefs, values, ctx) : { ok: true }

    // Use validated values when available, otherwise just store the raw values
    const valuesToStore = validated.resolved && Object.keys(validated.resolved).length > 0
      ? validated.resolved
      : values

    // Get current saved options and merge
    const state = storage.getState()
    const currentSaved = state.savedGameOptionsById?.[gameId] ?? emptySavedOptions()

    let nextSaved: GameSavedOptions
    if (scope === 'global') {
      nextSaved = {
        global: { ...currentSaved.global, ...valuesToStore },
        perPlayerCount: currentSaved.perPlayerCount,
      }
    } else {
      nextSaved = {
        global: currentSaved.global,
        perPlayerCount: {
          ...currentSaved.perPlayerCount,
          [playerCount!]: { ...currentSaved.perPlayerCount[playerCount!], ...valuesToStore },
        },
      }
    }

    // Update state
    const newState = {
      ...state,
      savedGameOptionsById: {
        ...state.savedGameOptionsById,
        [gameId]: nextSaved,
      },
    }
    storage.setState(newState)

    // Broadcast the change
    broadcastChannel.broadcast({
      type: 'optionsChanged',
      data: {
        gameId,
        savedOptions: nextSaved,
      },
    })

    return {
      success: validated.ok,
      error: validated.errors?.join(', '),
      savedOptions: nextSaved,
    }
  }

  /**
   * Get effective options for starting a game with a given player count.
   *
   * Combines global and per-player-count options with defaults.
   */
  const getEffectiveOptions = async (
    gameId: string,
    playerCount: number
  ): Promise<Record<string, unknown>> => {
    const state = storage.getState()
    const saved = state.savedGameOptionsById?.[gameId]
    const defs = (await resolveMetadata(gameId))?.options

    if (!defs || defs.length === 0) {
      return {}
    }

    return effectiveOptionsForCount(defs, saved, { playerCount })
  }

  /** The platform Host plays setting saved for a game, or its default. */
  const getHostPlays = (gameId: string): HostPlays =>
    normalizeHostPlays(storage.getState().savedGameOptionsById?.[gameId]?.global?.[HOST_PLAYS_OPTION_ID])

  return {
    getHostPlays,
    getSavedGameOptions,
    setSavedGameOptions,
    getEffectiveOptions,
  }
}

export type GameOptionsModule = ReturnType<typeof makeGameOptions>
